// src/PlayerStats.js
import React, { useState, useEffect, useMemo, useContext } from "react";
import { calculateTotals } from "./GameStatsTableHelper";
import { AuthContext } from "./App";
import { useTeam } from "./TeamContext";

export default function PlayerStats({ teamId: overrideTeamId }) {
  const { currentUser, accessToken } = useContext(AuthContext);
  const { selectedTeam } = useTeam();

  const teamId = overrideTeamId ?? selectedTeam?.id ?? currentUser?.team_id;
  const token = accessToken;

  const [stats, setStats] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "total_points", direction: "desc" });

  useEffect(() => {
    if (!teamId || !token) return;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch stats");

        const allStats = await res.json();
        const teamStats = allStats.filter((s) => s.team_id === teamId);
        setStats(teamStats);

        const uniquePlayers = Array.from(
          new Map(
            teamStats.map((s) => [
              s.player_id,
              { id: s.player_id, player_name: s.player_name, team_id: s.team_id },
            ])
          ).values()
        );
        setPlayers(uniquePlayers);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [teamId, token]);

  const statsByPlayer = players.map((player) => {
    const playerStats = stats.filter((s) => s.player_id === player.id);
    const totals = calculateTotals(playerStats);
    const total_seconds = totals.minutes || 0;
    const gamesPlayed = playerStats.filter((g) => (g.minutes || 0) * 60 + (g.seconds || 0) > 0).length;
    const mpgSeconds = gamesPlayed > 0 ? total_seconds / gamesPlayed : 0;

    return {
      player_name: player.player_name,
      ...totals,
      total_seconds,
      gp: gamesPlayed,
      mpg_seconds: mpgSeconds,
    };
  });

  const sortedPlayers = useMemo(() => {
    return [...statsByPlayer].sort((a, b) => {
      const getValue = (player, key) => {
        if (key === "player_name") return (player.player_name || "").toLowerCase();
        if (key === "minutes_formatted") return player.total_seconds;
        if (key === "mpg") return player.mpg_seconds;
        return player[key] ?? 0;
      };
      const aVal = getValue(a, sortConfig.key);
      const bVal = getValue(b, sortConfig.key);
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [statsByPlayer, sortConfig]);

  const headers = [
    { key: "player_name", label: "Player" },
    { key: "gp", label: "GP" },
    { key: "mpg", label: "MPG" },
    { key: "two_fg_attempts", label: "2FGA" },
    { key: "two_fg_made", label: "2FGM" },
    { key: "three_fg_attempts", label: "3PA" },
    { key: "three_fg_made", label: "3PM" },
    { key: "three_pt_percentage", label: "3P%" },
    { key: "ft_attempts", label: "FTA" },
    { key: "ft_made", label: "FTM" },
    { key: "ft_percentage", label: "FT%" },
    { key: "total_points", label: "PTS" },
    { key: "fg_attempts", label: "FGA" },
    { key: "fg_made", label: "FGM" },
    { key: "fg_percentage", label: "FG%" },
    { key: "off_rebounds", label: "OREB" },
    { key: "def_rebounds", label: "DREB" },
    { key: "treb", label: "TREB" },
    { key: "assists", label: "AST" },
    { key: "steals", label: "STL" },
    { key: "blocks", label: "BLK" },
    { key: "turnovers", label: "TO" },
    { key: "fouls", label: "FOUL" },
    { key: "minutes_formatted", label: "H:M:S" },
    { key: "bpm", label: "TPM" },
  ];

  if (loading) return <p className="text-center mt-6">Loading stats…</p>;
  if (!statsByPlayer.length) return <p className="text-center mt-6 text-gray-500">No stats available for this team.</p>;

  return (
    <div className="overflow-x-auto w-full px-4">
      <table className="table-fixed w-full border-collapse border border-gray-200 text-sm">
        <thead className="bg-gray-100 sticky top-0 z-10">
          <tr>
            {headers.map((h, index) => (
              <th
                key={h.key}
                className={`border px-2 py-2 cursor-pointer select-none font-medium ${
                  index === 0 ? "text-left" : "text-center"
                }`}
                onClick={() =>
                  setSortConfig({
                    key: h.key,
                    direction: sortConfig.key === h.key && sortConfig.direction === "asc" ? "desc" : "asc",
                  })
                }
              >
                {h.label} {sortConfig.key === h.key ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedPlayers.map((p) => (
            <tr key={p.player_name} className="even:bg-gray-50 hover:bg-gray-100">
              {headers.map((h, index) => {
                let value = p[h.key];
                if (h.key === "minutes_formatted") {
                  const hrs = Math.floor(p.total_seconds / 3600);
                  const mins = Math.floor((p.total_seconds % 3600) / 60);
                  const secs = p.total_seconds % 60;
                  value = `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
                }
                if (h.key === "mpg") {
                  const mins = Math.floor(p.mpg_seconds / 60);
                  const secs = Math.round(p.mpg_seconds % 60);
                  value = `${mins}:${secs.toString().padStart(2, "0")}`;
                }
                return (
                  <td
                    key={h.key}
                    className={`border px-2 py-1 ${index === 0 ? "text-left font-medium" : "text-center"}`}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
